import {
	Battery100Icon,
	BuildingStorefrontIcon,
	HomeModernIcon,
	WrenchScrewdriverIcon,
	ArrowPathRoundedSquareIcon,
	WrenchIcon,
} from '@heroicons/react/24/outline';

export const services = [
	{
		id: 'residential-solar',
		shortId: 'residential',
		title: 'Residential Solar',
		icon: HomeModernIcon,
		summary:
			'Custom rooftop solar for homeowners who want lower bills, clear numbers, and a system designed around the way they actually use power.',
		body: 'Rooftop solar for single-family homes across the Mid-Atlantic. Custom panel layout, the right inverter for your roof, and a finance plan that actually fits your budget.',
		points: [
			'Free electric-bill analysis',
			'Custom system design for your roof and usage',
			'Federal tax credit and local incentive guidance',
			'25-year production warranty options',
		],
		highlights: [
			'Roof, shade, and electric usage review',
			'Panel and inverter recommendations',
			'Loan, cash, and lease comparison',
			'Permitting, utility paperwork, and inspection support',
		],
		process: [
			'Review your latest utility bill and annual usage.',
			'Model system size, expected production, and savings.',
			'Finalize equipment, financing, permits, and install timing.',
			'Turn the system on and set up production monitoring.',
		],
		bestFor:
			'Homeowners with steady electric usage, usable roof space, and a goal of reducing long-term utility costs.',
	},
	{
		id: 'commercial-solar',
		shortId: 'commercial',
		title: 'Commercial Solar',
		icon: BuildingStorefrontIcon,
		summary:
			'Solar planning for businesses, nonprofits, and property owners that need clear ROI, reliable project management, and practical energy savings.',
		body: 'Cut your operating costs and demonstrate real sustainability. From small storefronts to industrial roofs, every project starts with usage data and a realistic payback model.',
		points: [
			'Energy audit and ROI modeling',
			'Roof-mount and ground-mount options',
			'Tax credit and depreciation guidance',
			'Production monitoring and reporting',
		],
		highlights: [
			'Interval data and demand-charge review',
			'Cash-flow and payback projections',
			'Tenant, owner, and facility constraint planning',
			'Utility interconnection coordination',
		],
		process: [
			'Review usage, rate class, and site constraints.',
			'Build a production model and financial projection.',
			'Coordinate engineering, permits, and utility approvals.',
			'Install, inspect, energize, and monitor performance.',
		],
		bestFor:
			'Businesses with high daytime energy use, owned facilities, or long-term occupancy plans.',
	},
	{
		id: 'roofing-installation',
		shortId: 'roofing',
		title: 'Roofing & Installation',
		icon: WrenchScrewdriverIcon,
		summary:
			'Coordinated roof and solar planning so the roof is ready before panels go up and warranties stay aligned.',
		body: 'Old roof? No problem. Roof replacement can be coordinated with the solar install so you only disrupt the property once and avoid moving panels later.',
		points: [
			'Roof inspection and replacement planning',
			'Coordinated install timeline',
			'Single point of contact',
			'Workmanship warranty included',
		],
		highlights: [
			'Roof age and condition review',
			'Panel layout that respects roof geometry',
			'Installer and roofing team coordination',
			'Flashing, mounting, and warranty alignment',
		],
		process: [
			'Confirm roof condition before final solar design.',
			'Plan replacement or repairs when needed.',
			'Coordinate roofing and solar crews around one schedule.',
			'Complete inspection, activation, and closeout documentation.',
		],
		bestFor:
			'Homeowners or businesses with an older roof, active leaks, or plans to replace shingles soon.',
	},
	{
		id: 'remove-reinstall',
		shortId: 'remove-reinstall',
		title: 'Remove & Reinstall',
		icon: ArrowPathRoundedSquareIcon,
		summary:
			'Professional removal and reinstall of an existing solar array — for roof repairs, roof replacement, or a move — handled so your panels and warranties come back intact.',
		body: 'Need the panels off and back on? Whether your roof needs work underneath them or you are relocating a system, removal and reinstallation is coordinated as one job so the array goes back up correctly and safely.',
		points: [
			'Safe de-installation and on-site storage',
			'Roof work coordinated in between',
			'Full reinstall and re-commissioning',
			'System tested and monitoring reconnected',
		],
		highlights: [
			'Panel, rail, and wiring condition check',
			'Labeling so everything returns to its place',
			'Coordination with your roofer or mover',
			'Production verified after reinstall',
		],
		process: [
			'Assess the array, roof, and reason for removal.',
			'Safely remove panels, racking, and wiring; store or transport them.',
			'Complete roof work or the move before reinstalling.',
			'Reinstall, re-energize, test output, and reconnect monitoring.',
		],
		bestFor:
			'Homeowners needing roof repair or replacement under existing panels, or anyone relocating a system.',
	},
	{
		id: 'maintenance',
		shortId: 'maintenance',
		title: 'Maintenance',
		icon: WrenchIcon,
		summary:
			'Regular upkeep, inspection, and cleaning so your system keeps producing at its best and small issues get caught before they cost you.',
		body: 'Solar is low-maintenance, not no-maintenance. Periodic inspection, cleaning, and a production check keep your array performing and your warranties in good standing.',
		points: [
			'Production and monitoring review',
			'Panel cleaning and inspection',
			'Inverter and wiring health check',
			'Issue diagnosis and repair coordination',
		],
		highlights: [
			'Compare actual vs. expected output',
			'Visual inspection of panels, racking, and seals',
			'Inverter fault and error-code review',
			'Recommendations before small issues grow',
		],
		process: [
			'Pull monitoring data and flag underperformance.',
			'Inspect panels, racking, wiring, and inverter on site.',
			'Clean and correct what needs attention.',
			'Confirm restored output and document the visit.',
		],
		bestFor:
			'System owners who want steady performance, early problem detection, and warranty peace of mind.',
	},
	{
		id: 'storage-backup',
		shortId: 'storage',
		title: 'Storage & Backup',
		icon: Battery100Icon,
		summary:
			'Battery backup options that keep key circuits running, improve resilience, and help you use more of the solar power you produce.',
		body: 'Pair solar with battery storage for stronger energy resilience. Keep the fridge, furnace, internet, and lights running when the grid goes down.',
		points: [
			'Whole-home or essential-loads design',
			'Battery sizing based on real usage',
			'Time-of-use rate optimization',
			'Smart monitoring app setup',
		],
		highlights: [
			'Outage priorities and critical-load planning',
			'Battery sizing and equipment comparison',
			'Solar-plus-storage wiring coordination',
			'Monitoring, app setup, and homeowner walkthrough',
		],
		process: [
			'Identify what needs to stay powered during an outage.',
			'Size storage around usage, budget, and backup goals.',
			'Coordinate design, permits, install, and inspection.',
			'Set operating modes and confirm monitoring is working.',
		],
		bestFor:
			'Customers who want backup power, more control over solar production, or protection from outages.',
	},
];

export const serviceAreas = [
	{
		id: 'maryland',
		name: 'Maryland',
		label: 'Solar Services in Maryland',
		summary:
			'Local solar guidance for Maryland homeowners and businesses navigating utility rates, SRECs, net metering, and permitting.',
		markets: [
			'Baltimore County',
			'Carroll County',
			'Howard County',
			'Anne Arundel County',
			'Harford County',
			'Frederick County',
			'Montgomery County',
			"Prince George's County",
		],
		details: [
			'Maryland solar projects often depend on roof condition, utility territory, local permitting, and SREC assumptions.',
			'Gabe helps compare system size, expected production, available incentives, and financing so the proposal matches the property.',
		],
		considerations: [
			'Maryland SREC value and net-metering assumptions',
			'BGE, Pepco, Potomac Edison, SMECO, and Delmarva utility coordination',
			'County permitting and HOA documentation',
		],
		benefits: [
			{
				title: 'Federal tax credit',
				description:
					'The 30% federal solar tax credit can cover a meaningful share of your system cost. Because this credit is tied to federal policy, Gabe walks through current eligibility and timing so you know what to expect on your return.',
			},
			{
				title: 'Sales and property tax breaks',
				description:
					'Maryland exempts solar equipment from the state’s 6% sales tax, and the added home value from your system is shielded from higher property taxes—so you gain value without a bigger tax bill.',
			},
			{
				title: 'SREC income',
				description:
					'Maryland’s Solar Renewable Energy Credit program lets your panels earn tradable credits as they produce clean power. Those credits can be sold for an extra stream of income on top of your bill savings.',
			},
			{
				title: 'Net metering',
				description:
					'When your system sends surplus power to the grid, your utility credits it back to your account—lowering what you owe during hours your panels produce less.',
			},
		],
	},
	{
		id: 'pennsylvania',
		name: 'Pennsylvania',
		label: 'Solar Services in Pennsylvania',
		summary:
			'Solar planning for Pennsylvania properties with clear production modeling, incentive review, and roof-first project coordination.',
		markets: ['York', 'Lancaster', 'Harrisburg', 'Philadelphia suburbs', 'Gettysburg', 'West Chester'],
		details: [
			'Pennsylvania customers need a practical look at electric rates, roof exposure, winter production, and long-term ownership plans.',
			'Each proposal starts with usage data and site conditions before equipment or financing is recommended.',
		],
		considerations: [
			'Utility interconnection requirements',
			'Roof pitch, snow load, and seasonal production',
			'Commercial depreciation and cash-flow modeling',
		],
		benefits: [
			{
				title: 'A range of state incentives',
				description:
					'Pennsylvania backs solar adoption through several programs, including net metering and Solar Renewable Energy Credits. Gabe helps sort out which ones apply to your property and how they affect your payback.',
			},
			{
				title: 'SREC earnings',
				description:
					'Each block of clean energy your system produces can generate Solar Renewable Energy Credits. Utilities buy these to meet their renewable targets, and their market value shifts with supply and demand—turning surplus generation into extra income.',
			},
			{
				title: 'Net metering',
				description:
					'On days your panels make more electricity than you use, the extra flows back to your utility and earns credits. Those credits then offset the power you pull from the grid when production dips.',
			},
			{
				title: 'Long-term rate protection',
				description:
					'Generating your own power reduces how much you buy from the utility, which insulates you from future rate increases and steadies your energy costs for decades.',
			},
		],
	},
	{
		id: 'virginia',
		name: 'Virginia',
		label: 'Solar Services in Virginia',
		summary:
			'Solar and battery consulting for Virginia homes and businesses that want stronger savings, cleaner power, and a managed install process.',
		markets: [
			'Northern Virginia',
			'Fairfax County',
			'Loudoun County',
			'Prince William County',
			'Arlington County',
			'Alexandria',
		],
		details: [
			'Virginia solar projects can vary widely by utility, county, roof type, and whether battery backup is part of the plan.',
			'Gabe helps define the right system size, equipment stack, and install path before paperwork begins.',
		],
		considerations: [
			'Dominion Energy and local utility coordination',
			'Battery backup and essential-load planning',
			'HOA, county, and inspection requirements',
		],
		benefits: [
			{
				title: 'Property tax exemption',
				description:
					'Solar raises a home’s value, and Virginia exempts that added value from property tax—so your system can boost what your property is worth without raising your tax bill.',
			},
			{
				title: 'A growing SREC market',
				description:
					'Virginia runs a mandatory Solar Renewable Energy Credit program. As demand for these credits climbs, their value is expected to rise—creating a real opportunity for system owners to earn from the power they produce.',
			},
			{
				title: 'Net metering',
				description:
					'Excess solar energy you send to the grid is banked as credits you can draw down later, letting you offset more of your overall electricity use across the year.',
			},
			{
				title: 'A state committed to renewables',
				description:
					'Virginia has set ambitious goals for expanding renewable energy and is investing in the infrastructure to get there—creating favorable conditions for both home and business solar.',
			},
		],
	},
	{
		id: 'dc',
		name: 'Washington DC',
		label: 'Solar Services in Washington DC',
		summary:
			'Solar guidance for DC rowhomes, businesses, and property owners where roof layout, permitting, and incentive assumptions matter.',
		markets: ['Capitol Hill', 'Petworth', 'Brookland', 'Georgetown', 'Navy Yard', 'Tenleytown'],
		details: [
			'DC solar projects often require careful design around compact roofs, access constraints, rowhome layouts, and local permitting.',
			'The goal is a clean proposal that explains production, incentives, equipment, and project timing in plain language.',
		],
		considerations: [
			'DC SREC and incentive assumptions',
			'Rowhome roof layout and access planning',
			'Permitting, utility paperwork, and inspection timing',
		],
		benefits: [
			{
				title: 'Strong local incentives',
				description:
					'Washington DC pairs solar with tax credits, grants, and rebates that can take a sizable bite out of your upfront installation cost. Gabe helps map which incentives fit your project.',
			},
			{
				title: 'Renewable Portfolio Standard',
				description:
					'DC’s Renewable Portfolio Standard requires utilities to source a set share of their power from renewables, which keeps steady demand—and value—behind the solar energy your system produces.',
			},
			{
				title: 'SREC value',
				description:
					'Solar Renewable Energy Credits let you sell the clean energy you generate back into the market. Their worth tracks utility demand for renewable credits, giving owners an ongoing way to earn from production.',
			},
			{
				title: 'Net metering',
				description:
					'Any surplus power your panels produce can be sold to the utility, offsetting the grid electricity you draw later and trimming your overall energy costs.',
			},
		],
	},
];

export function getService(id) {
	return services.find((service) => service.id === id || service.shortId === id);
}

export function getServiceArea(id) {
	return serviceAreas.find((area) => area.id === id);
}
